import { Sentry, SentryTeam } from 'lib-shared-sentry';
import { ApiErrorStatusCode } from '../axios-config';
import { parseAccessToken } from '../jwt';
import { noTokenRedirect } from '../redirect';
import { authManager } from './authManager';
async function addSentry401Breadcrumb(error) {
    const domain = window.location.origin.replace(/^https?:\/\//, '');
    const excludedDomains = ['localhost:8080', 'uptrends-monitor'];
    // Filter out uptrends-monitor and localhost:8080.
    if (!error.response ||
        excludedDomains.some((excludedDomain) => domain.includes(excludedDomain))) {
        return;
    }
    try {
        // Get the access token from the request header.
        const authHeader = error.response.config.headers.Authorization ?? '';
        const headerToken = authHeader
            ? authHeader.toString()?.split('Basic ')[1]
            : '';
        const accessToken = headerToken ? atob(headerToken).split('token:')[1] : '';
        const headerAccessTokenExp = accessToken
            ? parseAccessToken(accessToken).exp
            : 'None';
        // Get the access token from local storage.
        const authData = await authManager.getAuthData({ localStorageOnly: true });
        const currentTime = Math.floor(Date.now() / 1000);
        const authDataAccessToken = authData?.access_token;
        const authDataRefreshToken = authData?.refresh_token;
        const authDataAccessTokenExp = authDataAccessToken
            ? parseAccessToken(authDataAccessToken).exp
            : 'None';
        const authDataRefreshTokenExp = authDataRefreshToken
            ? parseAccessToken(authDataRefreshToken).exp
            : 'None';
        const accessTokenFromHeaderDiff = typeof headerAccessTokenExp === 'number'
            ? currentTime - headerAccessTokenExp
            : 'Unknown';
        const accessTokenFromLocalStorageDiff = typeof authDataAccessTokenExp === 'number'
            ? currentTime - authDataAccessTokenExp
            : 'Unknown';
        const refreshTokenFromLocalStorageDiff = typeof authDataRefreshTokenExp === 'number'
            ? currentTime - authDataRefreshTokenExp
            : 'Unknown';
        // X-Dixa-Rid header for Kibana lookup
        const xDixaRid = error.response.headers['x-dixa-rid'];
        Sentry.setContext('Expires', {
            currentTimestamp: currentTime,
            accessTokenFromHeader: headerAccessTokenExp,
            accessTokenFromHeaderDiff,
            accessTokenFromLocalStorage: authDataAccessTokenExp,
            accessTokenFromLocalStorageDiff,
            refreshTokenFromLocalStorage: authDataRefreshTokenExp,
            refreshTokenFromLocalStorageDiff,
            xDixaRid,
            navigatorOnline: navigator.onLine,
        });
    }
    catch (error) {
        console.error('Error adding Sentry error context', error);
    }
    Sentry.captureMessage('401 unauthorized interceptor', {
        level: 'debug',
        tags: {
            team: SentryTeam.Platform,
            domain,
        },
    });
}
const retryCountMap = new Map();
const max401Retries = 3;
/**
 * Adds a response interceptor to handle api errors.
 *
 * @param instance The axios instance.
 */
export function addErrorInterceptor(instance) {
    instance.interceptors.response.use((response) => {
        retryCountMap.delete(response.config.url);
        return response;
    }, (error) => {
        if (error.response) {
            const { status } = error.response;
            switch (status) {
                case ApiErrorStatusCode.Unauthorized:
                    if (!authManager.isRefreshTokenExpired() && error.config) {
                        // If only the access token is expired, we want to refresh it and retry the
                        // request.
                        Sentry.addBreadcrumb({
                            category: 'errorInterceptor',
                            message: 'Access token expired after request',
                            level: 'debug',
                        });
                        const retryCount = retryCountMap.get(error.config.url) ?? 0;
                        console.log(`Retrying request (${retryCount + 1}): ${error.config.url}`);
                        if (retryCount <= max401Retries) {
                            retryCountMap.set(error.config.url, retryCount + 1);
                            return instance(error.config);
                        }
                    }
                    addSentry401Breadcrumb(error);
                    console.error('401 unauthorized interceptor');
                    noTokenRedirect();
                    break;
            }
        }
        return Promise.reject(error);
    });
}
//# sourceMappingURL=interceptor.js.map